import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import type { ICardLayoutProps } from "../../common/card-layout/CardLayout";
import { CardLayout } from "../../common/card-layout/CardLayout";
import type { ExpandKeySuffix } from "../../common/card-layout/ExpandableText";
import { ExpandableText } from "../../common/card-layout/ExpandableText";
import { Link } from "../../common/card-layout/Link";

import type { ICommonNavigationParams } from "./useCardsNavigation";
import { useKeywordsNavigation } from "./useCardsNavigation";

interface IKeywordAlertProps extends Pick<ICardLayoutProps, "iconUrl">, ICommonNavigationParams {
    date: string;
    keywords: string[];
    text: ReactNode;
    title: ReactNode;
}

export const KeywordAlert = ({
    country,
    date,
    domain,
    iconUrl,
    keywords,
    text,
    title,
    trafficType,
}: IKeywordAlertProps) => {
    const { track } = useTracking();
    const { translate } = useTranslate();
    const { getKeywordLink, seeMoreKeywordsLink, navigateToKeywordsPage } = useKeywordsNavigation({
        domain,
        country,
        trafficType,
    });
    const handleExpand = (keySuffix: ExpandKeySuffix) => {
        track("Alert Card", "click", `${keySuffix} alert/alerts/keyword`);
    };

    return (
        <CardLayout
            iconUrl={iconUrl}
            onClick={navigateToKeywordsPage}
            data-automation="keyword-alert"
            data-domain={domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{translate("activation.alerts.cards.keyword.badge")}</Badge>
                <CardLayout.Title>{title}</CardLayout.Title>
                <Text
                    flexShrink={0}
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[250]}
                >
                    {date}
                </Text>
                <CardLayout.Arrow
                    tooltip={translate("activation.alerts.cards.keyword.arrow.tooltip")}
                    data-automation="alert-see-more"
                />
            </CardLayout.Header>
            <CardLayout.Content>
                <ExpandableText onTrack={handleExpand}>{text}</ExpandableText>
            </CardLayout.Content>
            <CardLayout.Footer
                label={translate("activation.alerts.cards.keyword.footer.label")}
                flexWrap="wrap"
            >
                {keywords.map((keyword) => (
                    <Link
                        key={keyword}
                        href={getKeywordLink(keyword.toLowerCase())}
                        onClick={() => {
                            track("Alert Card", "click", "open keyword/alerts/keyword");
                        }}
                    >
                        {keyword}
                    </Link>
                ))}
                <Link
                    href={seeMoreKeywordsLink}
                    flexShrink={0}
                    onClick={() => {
                        track("Alert Card", "click", "see more keywords/alerts/keyword");
                    }}
                    data-automation="see-more-link"
                >
                    {translate("activation.alerts.cards.common.footer.link")}
                </Link>
            </CardLayout.Footer>
        </CardLayout>
    );
};
